import { useCallback, useEffect, useRef, useState } from 'react';

function isInternalUserRole(config) {
  return String(config?.role || '').trim().toLowerCase() === 'internaluser';
}

function toCaseFileLogEntry(record, index, helpers, includeCaseScope = false) {
  const {
    getFirstRecordValue,
    getRecordMediaId,
    getRecordTranscriptFileId,
    getRecordFileType,
    getRecordScanStatus,
    getRecordLifecycleStatus
  } = helpers;

  const entry = {
    index,
    storedFileName: getFirstRecordValue(record, ['storedFileName', 'StoredFileName']),
    originalFileName: getFirstRecordValue(record, ['originalFileName', 'OriginalFileName']),
    mediaId: getRecordMediaId(record),
    transcriptFileId: getRecordTranscriptFileId(record),
    fileType: getRecordFileType(record),
    scanStatus: getRecordScanStatus(record),
    recordStatus: getRecordLifecycleStatus(record)
  };

  if (includeCaseScope) {
    entry.caseId = getFirstRecordValue(record, ['caseId', 'CaseId']);
    entry.applicationNumber = getFirstRecordValue(record, ['applicationNumber', 'ApplicationNumber']);
  }

  return entry;
}

function scopeFilesForCase(records, normalizedCaseId, normalizeDisplayName, getFirstRecordValue) {
  if (!normalizedCaseId) {
    return records;
  }

  return records.filter((record) => {
    const recordCaseId = normalizeDisplayName(getFirstRecordValue(record, ['caseId', 'CaseId']));
    return !recordCaseId || recordCaseId === normalizedCaseId;
  });
}

function getMergedNames(mergedItems) {
  return mergedItems
    .map((item) => item.mediaFile?.name)
    .filter(Boolean);
}

export function useCaseFiles({
  config,
  debugLog,
  managementApiService,
  normalizeCaseFileRecords,
  mapStatusSummaryFilesToCaseFileRecords,
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordTranscriptFileId,
  getRecordFileType,
  getRecordScanStatus,
  getRecordLifecycleStatus,
  dedupeCaseFileRecordsExact,
  collectSubmittedAtTimedOutRemoteScanCandidates,
  processTimedOutRemoteScanCandidates,
  refreshRemoteStatusSummary,
  resetRemoteStatusSummary,
  mergeRemoteStatuses,
  setMediaItems,
  mediaItemsRef,
  setInlineError,
  clearResolvedInlineWarningIfNeeded
}) {
  const [caseFiles, setCaseFiles] = useState([]);
  const [caseFilesLoading, setCaseFilesLoading] = useState(false);
  const [caseFilesError, setCaseFilesError] = useState('');
  const caseFilesRefreshPromiseRef = useRef(null);
  const caseFilesRef = useRef([]);
  const caseFileLogHelpers = {
    getFirstRecordValue,
    getRecordMediaId,
    getRecordTranscriptFileId,
    getRecordFileType,
    getRecordScanStatus,
    getRecordLifecycleStatus
  };

  useEffect(() => {
    caseFilesRef.current = caseFiles;
  }, [caseFiles]);

  const fetchCaseFiles = useCallback(async () => {
    if (isInternalUserRole(config)) {
      const response = await managementApiService.getInternalCaseFiles(config.caseId);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = payload?.error?.message || `files query failed (${response.status})`;
        throw new Error(message);
      }

      return normalizeCaseFileRecords(mapStatusSummaryFilesToCaseFileRecords(payload?.data, payload));
    }

    const response = await managementApiService.getApplicationFiles({
      clientId: config.clientId,
      applicationNumber: config.applicationNumber,
      caseId: config.caseId
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = payload?.error?.message || `files query failed (${response.status})`;
      throw new Error(message);
    }

    return normalizeCaseFileRecords(Array.isArray(payload?.data) ? payload.data : []);
  }, [config.caseId, config.clientId, config.applicationNumber, config.role, managementApiService, mapStatusSummaryFilesToCaseFileRecords, normalizeCaseFileRecords]);

  const updateMediaItemsWithRemoteStatuses = useCallback((scopedFiles, dedupedScopedFiles) => {
    setMediaItems((currentItems) => {
      const mergedItems = mergeRemoteStatuses(currentItems, scopedFiles);
      debugLog('load-case-files-merge', {
        currentCount: currentItems.length,
        remoteCount: scopedFiles.length,
        mergedCount: mergedItems.length,
        mergedNames: getMergedNames(mergedItems)
      });

      mediaItemsRef.current = mergedItems;
      setInlineError((currentMessage) => clearResolvedInlineWarningIfNeeded(currentMessage, mergedItems, dedupedScopedFiles));
      return mergedItems;
    });
  }, [
    clearResolvedInlineWarningIfNeeded,
    debugLog,
    mediaItemsRef,
    mergeRemoteStatuses,
    setInlineError,
    setMediaItems
  ]);

  const loadCaseFiles = useCallback(async (showLoading = true) => {
    const invocationId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const startSnapshot = {
      invocationId,
      showLoading,
      hasBaseUrl: Boolean(managementApiService?.baseUrl),
      caseId: config.caseId || '',
      clientId: config.clientId || '',
      applicationNumber: config.applicationNumber || '',
      role: config.role || '',
      refreshInFlight: Boolean(caseFilesRefreshPromiseRef.current)
    };

    debugLog('load-case-files-called', startSnapshot);

    if (!managementApiService?.baseUrl || !config.caseId) {
      setCaseFiles([]);
      setCaseFilesError('');
      resetRemoteStatusSummary();
      debugLog('load-case-files-skipped', {
        ...startSnapshot,
        reason: 'missing-base-url-or-case-id'
      });
      return false;
    }

    if (caseFilesRefreshPromiseRef.current) {
      debugLog('load-case-files-joined-existing-refresh', startSnapshot);
      try {
        await caseFilesRefreshPromiseRef.current;
        return true;
      } catch {
        return false;
      }
    }

    if (showLoading) {
      setCaseFilesLoading(true);
    }

    const refreshPromise = (async () => {
      const files = await fetchCaseFiles();
      debugLog('load-case-files-api-response', {
        totalCount: files.length,
        files: files.map((record, index) => toCaseFileLogEntry(record, index, caseFileLogHelpers, true))
      });

      const normalizedCaseId = normalizeDisplayName(config.caseId);
      let scopedFiles = scopeFilesForCase(files, normalizedCaseId, normalizeDisplayName, getFirstRecordValue);

      debugLog('load-case-files-scoped', {
        caseId: config.caseId,
        scopedCount: scopedFiles.length,
        scopedFiles: scopedFiles.map((record, index) => toCaseFileLogEntry(record, index, caseFileLogHelpers))
      });

      let dedupedScopedFiles = dedupeCaseFileRecordsExact(scopedFiles);
      const timedOutRemoteScanCandidates = collectSubmittedAtTimedOutRemoteScanCandidates(dedupedScopedFiles);

      if (timedOutRemoteScanCandidates.length > 0) {
        const updatedTimedOutRecords = await processTimedOutRemoteScanCandidates(timedOutRemoteScanCandidates);
        debugLog('load-case-files-processing-timeout-candidates', {
          candidateCount: timedOutRemoteScanCandidates.length,
          updatedTimedOutRecords,
          candidates: timedOutRemoteScanCandidates
        });

        if (updatedTimedOutRecords) {
          const refreshedFiles = await fetchCaseFiles();
          scopedFiles = scopeFilesForCase(refreshedFiles, normalizedCaseId, normalizeDisplayName, getFirstRecordValue);
          dedupedScopedFiles = dedupeCaseFileRecordsExact(scopedFiles);
          debugLog('load-case-files-refreshed-after-timeout-update', {
            totalCount: refreshedFiles.length,
            scopedCount: scopedFiles.length,
            dedupedCount: dedupedScopedFiles.length
          });
        }
      }

      debugLog('load-case-files-deduped', {
        dedupedCount: dedupedScopedFiles.length,
        dedupedFiles: dedupedScopedFiles.map((record, index) => toCaseFileLogEntry(record, index, caseFileLogHelpers))
      });

      caseFilesRef.current = dedupedScopedFiles;
      setCaseFiles(dedupedScopedFiles);
      debugLog('load-case-files-before-remote-summary-refresh', {
        invocationId,
        dedupedCount: dedupedScopedFiles.length
      });
      await refreshRemoteStatusSummary();
      updateMediaItemsWithRemoteStatuses(scopedFiles, dedupedScopedFiles);
      setCaseFilesError('');
      debugLog('load-case-files-completed', {
        invocationId,
        dedupedCount: dedupedScopedFiles.length
      });
      return dedupedScopedFiles;
    })();

    caseFilesRefreshPromiseRef.current = refreshPromise;

    try {
      return await refreshPromise;
    } catch (error) {
      debugLog('load-case-files-failed', {
        invocationId,
        message: error?.message || 'Unknown error'
      });
      setCaseFilesError(`Unable to load case files: ${error?.message || 'Unknown error'}`);
      return false;
    } finally {
      if (caseFilesRefreshPromiseRef.current === refreshPromise) {
        caseFilesRefreshPromiseRef.current = null;
      }

      if (showLoading) {
        setCaseFilesLoading(false);
      }
    }
  }, [
    clearResolvedInlineWarningIfNeeded,
    collectSubmittedAtTimedOutRemoteScanCandidates,
    config.caseId,
    config.clientId,
    config.applicationNumber,
    config.role,
    debugLog,
    dedupeCaseFileRecordsExact,
    fetchCaseFiles,
    getFirstRecordValue,
    getRecordFileType,
    getRecordLifecycleStatus,
    getRecordMediaId,
    getRecordScanStatus,
    getRecordTranscriptFileId,
    mediaItemsRef,
    mergeRemoteStatuses,
    normalizeDisplayName,
    processTimedOutRemoteScanCandidates,
    refreshRemoteStatusSummary,
    resetRemoteStatusSummary,
    updateMediaItemsWithRemoteStatuses
  ]);

  return {
    caseFiles,
    caseFilesLoading,
    caseFilesError,
    caseFilesRef,
    setCaseFiles,
    setCaseFilesError,
    fetchCaseFiles,
    loadCaseFiles
  };
}
